import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { useAuth } from '../../contexts/AuthContext';
import { Users, Calendar, BookOpen, BarChart3, Settings, LogOut } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { username, logout, getAuthHeaders } = useAuth();
  const [stats, setStats] = useState({
    teamMembers: 0,
    events: 0,
    blogPosts: 0
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const headers = getAuthHeaders();
      
      const [teamResponse, eventsResponse, blogResponse] = await Promise.all([
        axios.get(`${API}/admin/team`, { headers }),
        axios.get(`${API}/admin/events`, { headers }),
        axios.get(`${API}/admin/blog`, { headers })
      ]);

      setStats({
        teamMembers: teamResponse.data.length,
        events: eventsResponse.data.length,
        blogPosts: blogResponse.data.length
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const dashboardCards = [
    {
      title: 'Team Members',
      value: stats.teamMembers,
      icon: Users,
      link: '/admin/team',
      color: 'bg-blue-500'
    },
    {
      title: 'Events',
      value: stats.events,
      icon: Calendar,
      link: '/admin/events',
      color: 'bg-green-500'
    },
    {
      title: 'Blog Posts',
      value: stats.blogPosts,
      icon: BookOpen,
      link: '/admin/blog',
      color: 'bg-purple-500'
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
              <p className="text-gray-600">Welcome back, {username}</p>
            </div>
            <div className="flex items-center space-x-4">
              <Button
                variant="outline"
                asChild
                className="flex items-center space-x-2"
              >
                <Link to="/admin/settings">
                  <Settings className="h-4 w-4" />
                  <span>Settings</span>
                </Link>
              </Button>
              <Button
                variant="outline"
                onClick={logout}
                className="flex items-center space-x-2"
              >
                <LogOut className="h-4 w-4" />
                <span>Logout</span>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Dashboard Content */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          {dashboardCards.map((card, index) => (
            <Card key={index} className="hover:shadow-lg transition-shadow border-none bg-white">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">
                  {card.title}
                </CardTitle>
                <div className={`p-2 rounded-lg ${card.color}`}>
                  <card.icon className="h-4 w-4 text-white" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-gray-900 mb-2">
                  {isLoading ? '...' : card.value}
                </div>
                <Button
                  variant="ghost"
                  className="p-0 h-auto text-[#0b2c4a] hover:text-[#0b2c4a]/80"
                  asChild
                >
                  <Link to={card.link}>Manage →</Link>
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Quick Actions */}
        <Card className="border-none bg-white">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Settings className="h-5 w-5 text-[#0b2c4a]" />
              <span>Quick Actions</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              <Button
                className="bg-[#0b2c4a] hover:bg-[#0b2c4a]/90 text-white h-auto py-4"
                asChild
              >
                <Link to="/admin/team/new" className="flex flex-col items-center space-y-2">
                  <Users className="h-6 w-6" />
                  <span>Add Team Member</span>
                </Link>
              </Button>
              
              <Button
                variant="outline"
                className="border-[#0b2c4a] text-[#0b2c4a] hover:bg-[#0b2c4a] hover:text-white h-auto py-4"
                asChild
              >
                <Link to="/admin/events/new" className="flex flex-col items-center space-y-2">
                  <Calendar className="h-6 w-6" />
                  <span>Create Event</span>
                </Link>
              </Button>
              
              <Button
                variant="outline"
                className="border-[#0b2c4a] text-[#0b2c4a] hover:bg-[#0b2c4a] hover:text-white h-auto py-4"
                asChild
              >
                <Link to="/admin/blog/new" className="flex flex-col items-center space-y-2">
                  <BookOpen className="h-6 w-6" />
                  <span>Write Article</span>
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card className="mt-8 border-none bg-white">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <BarChart3 className="h-5 w-5 text-[#0b2c4a]" />
              <span>System Status</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                <span className="text-green-800">Database Connection</span>
                <span className="text-green-600 font-medium">✓ Connected</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <span className="text-blue-800">API Status</span>
                <span className="text-blue-600 font-medium">✓ Online</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <span className="text-gray-800">Last Login</span>
                <span className="text-gray-600 font-medium">Just now</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminDashboard;